import { useEffect, useRef } from 'react';
import { createFallbackBotAction, decideBotAction } from '../domain/botAI';
import { HUMAN_PLAYER_INDEX } from '../domain/types';
import { useGameStore } from '../store/gameStore';

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function isBotActionNeeded(phase: string, activeIndex: number, settlementIndex: number): boolean {
  switch (phase) {
    case 'mission_draft':
    case 'mission_select':
    case 'betting':
    case 'draft':
      return activeIndex !== HUMAN_PLAYER_INDEX;
    case 'settlement':
      return settlementIndex !== HUMAN_PLAYER_INDEX;
    default:
      return false;
  }
}

export function useGameLoop(): void {
  const game = useGameStore((s) => s.game);
  const dispatch = useGameStore((s) => s.dispatch);
  const busyRef = useRef(false);

  useEffect(() => {
    if (busyRef.current) return;
    if (game.phase === 'setup' || game.phase === 'game_over' || game.phase === 'final_score') {
      return;
    }
    if (game.phase === 'round_roll') return;

    const needsBot =
      isBotActionNeeded(game.phase, game.activePlayerIndex, game.settlementIndex) ||
      (game.phase === 'mission_draft' && game.activePlayerIndex !== HUMAN_PLAYER_INDEX);

    if (!needsBot) {
      useGameStore.getState().setBotActingPlayerIndex(null);
      return;
    }

    busyRef.current = true;

    const run = async () => {
      const { botSpeedMs, setBotActingPlayerIndex } = useGameStore.getState();
      const actorIndex =
        game.phase === 'settlement' ? game.settlementIndex : game.activePlayerIndex;

      setBotActingPlayerIndex(actorIndex);

      try {
        await sleep(botSpeedMs);

        const current = useGameStore.getState().game;
        let action = decideBotAction(current);
        if (!action) {
          action = createFallbackBotAction(current);
        }
        if (action) {
          dispatch(action);
        }
      } catch (error) {
        console.error('[useGameLoop]', error);
        const current = useGameStore.getState().game;
        const fallback = createFallbackBotAction(current);
        if (fallback) {
          dispatch(fallback);
        }
      } finally {
        setBotActingPlayerIndex(null);
        busyRef.current = false;
      }
    };

    void run();
  }, [game, dispatch]);
}
